import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Send, Save, RefreshCw, Undo2, Plus, X, GripVertical, ChevronDown, ChevronRight, Gift, Trash2, Lightbulb } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const CATEGORY_ORDER = [
  "Managing Performance & Accountability",
  "Emotional & Human Moments",
  "Team Dynamics & Relationships",
  "Communication & Transparency",
  "Identity, Authority & Leadership Presence",
  "Career & Transitions",
  "Managing Up & Organizational Reality",
  "Self-Management for Managers",
  "Managing Change",
];

const SHORT_FIELDS = [
  ["title", "Title"],
  ["category", "Category"],
  ["tagline", "Tagline"],
  ["preview", "Preview (used on cards)"],
];
const TEXTAREA_FIELDS = [["manager_reminder", "Manager reminder (optional — leave empty to hide)"]];
const LIST_FIELDS = [
  ["general_context", "General context to consider"],
  ["why_challenging", "Why this can be challenging"],
  ["what_matters_most", "What matters most"],
  ["immediate_considerations", "Immediate considerations"],
  ["ground_yourself", "Ground yourself — self-reflection questions"],
  ["what_makes_harder", "Common instincts to avoid"],
  ["escalation_considerations", "Additional support may be needed when"],
  ["after_conversation", "After the conversation"],
];
const GROUPED_FIELDS = [
  ["helpful_language", "Helpful language"],
  ["follow_through", "Follow through"],
];

const FieldLabel = ({ children }) => (
  <label
    className="block mb-2 text-[11px] font-bold uppercase tracking-[0.18em]"
    style={{ color: "var(--mek-text-2)" }}
  >
    {children}
  </label>
);

/**
 * SeeAlsoPicker — searchable, click-to-add cross-link editor.
 *
 * Renders the currently selected scenarios as removable chips at the top,
 * and a live-filtered list of remaining scenarios underneath. Clicking
 * a row appends its slug to the value array; clicking the × on a chip
 * removes it. The current scenario (you can't link to yourself) and any
 * already-selected scenarios are filtered out of the suggestion list.
 *
 * Scenarios are grouped by category for fast browsing, and a leading
 * "Same category" group surfaces the most likely candidates at the top.
 */
function SeeAlsoPicker({ value, options, currentSlug, onChange }) {
  const [query, setQuery] = useState("");
  const selectedSet = new Set(value || []);
  const current = options.find((s) => s.slug === currentSlug);
  const currentCategory = current?.category;

  const byTitleAsc = (a, b) => (a.title || "").localeCompare(b.title || "");

  // Filter + group the picker list.
  const available = options.filter(
    (s) => s.slug !== currentSlug && !selectedSet.has(s.slug),
  );
  const q = query.trim().toLowerCase();
  const filtered = q
    ? available.filter(
        (s) =>
          (s.title || "").toLowerCase().includes(q) ||
          (s.category || "").toLowerCase().includes(q) ||
          (s.tagline || "").toLowerCase().includes(q),
      )
    : available;

  // Same-category group surfaces near-duplicates / sibling scenarios first.
  const sameCategory = filtered
    .filter((s) => currentCategory && s.category === currentCategory)
    .sort(byTitleAsc);
  const rest = filtered
    .filter((s) => !currentCategory || s.category !== currentCategory)
    .sort(byTitleAsc);

  const addSlug = (slug) => {
    if ((value || []).includes(slug)) return;
    onChange([...(value || []), slug]);
  };
  const removeSlug = (slug) => onChange((value || []).filter((s) => s !== slug));
  const moveSlug = (idx, dir) => {
    const arr = [...(value || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    onChange(arr);
  };

  const titleFor = (slug) =>
    options.find((s) => s.slug === slug)?.title || slug;
  const categoryFor = (slug) =>
    options.find((s) => s.slug === slug)?.category || "";

  return (
    <div data-testid="see-also-picker">
      {/* Selected chips with reorder controls */}
      <div className="mb-4">
        {(value || []).length === 0 ? (
          <p
            className="text-sm italic px-3 py-2.5 rounded-md border border-dashed"
            style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-3)" }}
            data-testid="see-also-empty"
          >
            No cross-links yet. Pick scenarios below to add some.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="see-also-selected">
            {(value || []).map((slug, idx) => (
              <li
                key={slug}
                className="flex items-center gap-2 px-3 py-2 rounded-md border bg-white"
                style={{ borderColor: "var(--mek-border)" }}
                data-testid={`see-also-chip-${slug}`}
              >
                <span
                  className="text-[10px] font-bold tracking-[0.14em] uppercase w-6 text-center"
                  style={{ color: "var(--mme-teal)" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--mek-text)" }}>
                    {titleFor(slug)}
                  </p>
                  <p className="text-xs truncate" style={{ color: "var(--mek-text-3)" }}>
                    {categoryFor(slug)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => moveSlug(idx, -1)}
                  disabled={idx === 0}
                  className="text-xs px-1.5 py-0.5 opacity-60 hover:opacity-100 disabled:opacity-20"
                  title="Move up"
                  data-testid={`see-also-up-${slug}`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveSlug(idx, 1)}
                  disabled={idx === (value || []).length - 1}
                  className="text-xs px-1.5 py-0.5 opacity-60 hover:opacity-100 disabled:opacity-20"
                  title="Move down"
                  data-testid={`see-also-down-${slug}`}
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => removeSlug(slug)}
                  className="text-xs opacity-60 hover:opacity-100 flex items-center"
                  style={{ color: "var(--mme-plum)" }}
                  title="Remove cross-link"
                  data-testid={`see-also-remove-${slug}`}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Search + suggestion list */}
      <div
        className="border rounded-md"
        style={{ borderColor: "var(--mek-border)", background: "rgba(245, 239, 227, 0.45)" }}
      >
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by title or category…"
          className="w-full px-3 py-2.5 text-sm bg-transparent border-b focus:outline-none"
          style={{ borderColor: "var(--mek-border)" }}
          data-testid="see-also-search"
        />
        <div className="max-h-64 overflow-y-auto" data-testid="see-also-suggestions">
          {filtered.length === 0 ? (
            <p
              className="text-sm italic px-3 py-4 text-center"
              style={{ color: "var(--mek-text-3)" }}
            >
              {q ? "No matches." : "Nothing left to link."}
            </p>
          ) : (
            <>
              {sameCategory.length > 0 && (
                <PickerGroup
                  label={`Same category — ${currentCategory || ""}`}
                  items={sameCategory}
                  onPick={addSlug}
                  emphasize
                />
              )}
              {rest.length > 0 && (
                <PickerGroup
                  label={sameCategory.length > 0 ? "Other categories" : "All scenarios"}
                  items={rest}
                  onPick={addSlug}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PickerGroup({ label, items, onPick, emphasize = false }) {
  return (
    <div>
      <p
        className="text-[10px] font-bold uppercase tracking-[0.18em] px-3 pt-3 pb-1.5"
        style={{ color: emphasize ? "var(--mme-teal)" : "var(--mek-text-3)" }}
      >
        {label}
      </p>
      <ul>
        {items.map((s) => (
          <li key={s.slug}>
            <button
              type="button"
              onClick={() => onPick(s.slug)}
              className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-white transition-colors"
              data-testid={`see-also-option-${s.slug}`}
            >
              <Plus size={13} style={{ color: "var(--mme-teal)" }} />
              <span className="flex-1 min-w-0">
                <span
                  className="block text-sm font-medium truncate"
                  style={{ color: "var(--mek-text)" }}
                >
                  {s.title}
                </span>
                <span className="block text-xs truncate" style={{ color: "var(--mek-text-3)" }}>
                  {s.category}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Admin() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [compGrants, setCompGrants] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [pendingAnnouncements, setPendingAnnouncements] = useState({ pending: [], recipient_count: 0 });
  const [announcing, setAnnouncing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  // In-memory undo: maps slug → previous saved version
  const [undoMap, setUndoMap] = useState({});
  // Drag-and-drop reorder state
  const [draggingSlug, setDraggingSlug] = useState(null);
  const [dragOverSlug, setDragOverSlug] = useState(null);
  // Per-category collapse state (key: category name). Empty/missing = expanded.
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const toggleCategory = (category) =>
    setCollapsedCategories((m) => ({ ...m, [category]: !m[category] }));

  useEffect(() => {
    if (auth.loading) return;
    if (auth.role !== "admin") return navigate("/admin/login");
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.loading, auth.role]);

  const loadAll = async () => {
    try {
      const [sc, cu, tx, cg, an, pa] = await Promise.all([
        api.get("/library/scenarios"),
        api.get("/admin/customers"),
        api.get("/admin/transactions"),
        api.get("/admin/comp-access"),
        api.get(`/admin/analytics?days=${analyticsDays}`),
        api.get("/admin/pending-announcements"),
      ]);
      setScenarios(sc.data);
      setCustomers(cu.data);
      setTransactions(tx.data);
      setCompGrants(cg.data);
      setAnalytics(an.data);
      setPendingAnnouncements(pa.data);
    } catch (e) {
      toast.error("Couldn't load admin data.");
    }
  };

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of scenarios) {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category).push(s);
    }
    // Sort within each category by display_order, then title.
    for (const [, items] of map.entries()) {
      items.sort((a, b) => {
        const ao = a.display_order ?? 1000;
        const bo = b.display_order ?? 1000;
        if (ao !== bo) return ao - bo;
        return (a.title || "").localeCompare(b.title || "");
      });
    }
    const ordered = [];
    for (const cat of CATEGORY_ORDER) {
      if (map.has(cat)) {
        ordered.push([cat, map.get(cat)]);
        map.delete(cat);
      }
    }
    for (const [cat, items] of map.entries()) ordered.push([cat, items]);
    return ordered;
  }, [scenarios]);

  const openScenario = async (slug) => {
    setActiveSlug(slug);
    try {
      const { data } = await api.get(`/admin/scenarios/${slug}`);
      setDraft(data);
    } catch {
      toast.error("Couldn't load that scenario.");
    }
  };

  // Reorder within a single category. `fromSlug` is dragged onto `toSlug`.
  // Both must belong to the same category for the move to apply.
  const reorderWithinCategory = async (fromSlug, toSlug) => {
    if (!fromSlug || !toSlug || fromSlug === toSlug) return;
    const from = scenarios.find((s) => s.slug === fromSlug);
    const to = scenarios.find((s) => s.slug === toSlug);
    if (!from || !to || from.category !== to.category) return;

    // Build current category order, then move `from` to the index of `to`.
    const list = scenarios
      .filter((s) => s.category === from.category)
      .slice()
      .sort((a, b) => (a.display_order ?? 1000) - (b.display_order ?? 1000));
    const fromIdx = list.findIndex((s) => s.slug === fromSlug);
    const toIdx = list.findIndex((s) => s.slug === toSlug);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = list.splice(fromIdx, 1);
    list.splice(toIdx, 0, moved);

    // Re-stamp display_order in increments of 10 for clean spacing.
    const updates = list.map((s, i) => ({ slug: s.slug, display_order: (i + 1) * 10 }));

    // Optimistic local update.
    const updateMap = new Map(updates.map((u) => [u.slug, u.display_order]));
    setScenarios((prev) =>
      prev.map((s) => (updateMap.has(s.slug) ? { ...s, display_order: updateMap.get(s.slug) } : s))
    );

    try {
      await api.post("/admin/scenarios/reorder", { updates });
      toast.success("Order updated.");
    } catch {
      toast.error("Couldn't save the new order. Reloading.");
      loadAll();
    }
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      // Capture pre-save snapshot for undo
      const snapshot = scenarios.find((s) => s.slug === draft.slug);
      const fullSnapshot = snapshot ? await api.get(`/admin/scenarios/${draft.slug}`).then((r) => r.data) : null;
      await api.put(`/admin/scenarios/${draft.slug}`, draft);
      if (fullSnapshot) {
        setUndoMap((m) => ({ ...m, [draft.slug]: fullSnapshot }));
      }
      toast.success("Saved.", {
        description: "Live on the library now.",
        action: fullSnapshot
          ? {
              label: "Undo",
              onClick: () => undoLastSave(draft.slug),
            }
          : undefined,
      });
      await loadAll();
    } catch (e) {
      toast.error("Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const undoLastSave = async (slug) => {
    const prev = undoMap[slug];
    if (!prev) return;
    try {
      await api.put(`/admin/scenarios/${slug}`, prev);
      setDraft(prev);
      setUndoMap((m) => {
        const copy = { ...m };
        delete copy[slug];
        return copy;
      });
      toast.success("Reverted to previous version.");
      await loadAll();
    } catch {
      toast.error("Couldn't undo. Re-save manually if needed.");
    }
  };

  const resendLink = async (email) => {
    try {
      await api.post(`/admin/customers/${encodeURIComponent(email)}/resend-link`);
      toast.success(`Access link sent to ${email}`);
    } catch {
      toast.error("Couldn't send link.");
    }
  };

  const sendTestPurchaseNotification = async () => {
    try {
      const { data } = await api.post("/admin/test-purchase-notification");
      if (data?.ok) {
        toast.success(`Test notification sent to ${data.sent_to}`);
      } else {
        toast.error("Resend didn't accept the email. Check the backend logs.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send test notification.");
    }
  };

  const sendTestBuyerEmail = async () => {
    try {
      const { data } = await api.post("/admin/test-buyer-purchase-email");
      if (data?.ok) {
        toast.success(`Buyer welcome email sent to ${data.sent_to}`);
      } else {
        toast.error("Resend didn't accept the email. Check the backend logs.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send buyer test email.");
    }
  };

  const previewReceiptPdf = async () => {
    try {
      const res = await api.get("/admin/preview-receipt.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("Couldn't render receipt preview.");
    }
  };

  const resendGift = async (sessionId, recipient) => {
    const ok = window.confirm(
      `Resend the gift toolkit email to ${recipient}?\n\n` +
        `They will receive a fresh access link. Sender name and note are preserved.`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(
        `/admin/transactions/${encodeURIComponent(sessionId)}/resend-gift`
      );
      if (data?.ok) {
        toast.success(`Gift email resent to ${recipient}.`);
      } else {
        toast.error(
          `Resend rejected the email for ${recipient}. Check the backend logs / Resend dashboard.`
        );
      }
      // Refresh transactions to reflect the new "Delivered"/"Failed" status
      try {
        const tx = await api.get("/admin/transactions");
        setTransactions(tx.data);
      } catch (_) {
        /* non-critical */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend gift email.");
    }
  };

  const announceNewScenarios = async () => {
    const count = pendingAnnouncements.pending.length;
    const recipients = pendingAnnouncements.recipient_count;
    if (!count) {
      toast.info("No new scenarios to announce.");
      return;
    }
    const confirmed = window.confirm(
      `Send an announcement email to ${recipients} customer${recipients === 1 ? "" : "s"} ` +
        `about ${count} new scenario${count === 1 ? "" : "s"}?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    setAnnouncing(true);
    try {
      const { data } = await api.post("/admin/announce-new-scenarios");
      toast.success(
        `Sent to ${data.sent} customer${data.sent === 1 ? "" : "s"}. ` +
          `${data.scenarios_announced} scenario${data.scenarios_announced === 1 ? "" : "s"} marked as announced.`
      );
      // Refresh pending list (should be empty now)
      const pa = await api.get("/admin/pending-announcements");
      setPendingAnnouncements(pa.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send announcement.");
    } finally {
      setAnnouncing(false);
    }
  };

  const markAllAnnounced = async () => {
    const count = pendingAnnouncements.pending.length;
    if (!count) {
      toast.info("Nothing pending.");
      return;
    }
    const confirmed = window.confirm(
      `Mark all ${count} pending scenario${count === 1 ? "" : "s"} as already announced (no email sent)?\n\n` +
        `Use this if you've added scenarios but don't want to email anyone about them.`
    );
    if (!confirmed) return;
    try {
      const { data } = await api.post("/admin/mark-all-announced");
      toast.success(`Marked ${data.marked} scenario${data.marked === 1 ? "" : "s"} as announced.`);
      const pa = await api.get("/admin/pending-announcements");
      setPendingAnnouncements(pa.data);
    } catch (e) {
      toast.error("Couldn't mark as announced.");
    }
  };

  const previewAnnouncementEmail = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/admin/preview-announcement", {
        responseType: "text",
        transformResponse: [(d) => d],
      });
      setPreviewHtml(typeof data === "string" && data ? data : "<p>Preview unavailable.</p>");
    } catch (e) {
      toast.error("Couldn't render preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const updateField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));
  const updateListField = (field, idx, value) => {
    setDraft((d) => {
      const list = [...(d[field] || [])];
      list[idx] = value;
      return { ...d, [field]: list };
    });
  };
  const addListItem = (field) =>
    setDraft((d) => ({ ...d, [field]: [...(d[field] || []), ""] }));
  const removeListItem = (field, idx) =>
    setDraft((d) => ({ ...d, [field]: d[field].filter((_, i) => i !== idx) }));

  /**
   * toggleProTip — adds or removes the "Pro tip: " prefix on a list item.
   * The frontend renderer turns any item starting with that prefix into a
   * gold callout box, so this is the editor-friendly way to mark a line.
   */
  const toggleProTip = (field, idx) =>
    setDraft((d) => {
      const list = [...(d[field] || [])];
      const current = (list[idx] || "").trim();
      const tipRe = /^pro[\s-]*tip\s*[:\-—]\s*/i;
      if (tipRe.test(current)) {
        // Already a Pro tip — strip the marker so it becomes a normal item.
        list[idx] = current.replace(tipRe, "");
      } else {
        // Mark as a Pro tip — capitalize cleanly.
        list[idx] = `Pro tip: ${current}`;
      }
      return { ...d, [field]: list };
    });

  // Grouped field handlers (for helpful_language + follow_through).
  const updateGroupLabel = (field, gi, label) =>
    setDraft((d) => {
      const groups = [...(d[field] || [])];
      groups[gi] = { ...groups[gi], label };
      return { ...d, [field]: groups };
    });
  const updateGroupItem = (field, gi, ii, value) =>
    setDraft((d) => {
      const groups = [...(d[field] || [])];
      const items = [...(groups[gi].items || [])];
      items[ii] = value;
      groups[gi] = { ...groups[gi], items };
      return { ...d, [field]: groups };
    });
  const addGroup = (field) =>
    setDraft((d) => ({ ...d, [field]: [...(d[field] || []), { label: "", items: [""] }] }));
  const removeGroup = (field, gi) =>
    setDraft((d) => ({ ...d, [field]: d[field].filter((_, i) => i !== gi) }));
  const addGroupItem = (field, gi) =>
    setDraft((d) => {
      const groups = [...(d[field] || [])];
      groups[gi] = { ...groups[gi], items: [...(groups[gi].items || []), ""] };
      return { ...d, [field]: groups };
    });
  const removeGroupItem = (field, gi, ii) =>
    setDraft((d) => {
      const groups = [...(d[field] || [])];
      groups[gi] = {
        ...groups[gi],
        items: groups[gi].items.filter((_, i) => i !== ii),
      };
      return { ...d, [field]: groups };
    });

  // Framework handlers (frameworks = list of {title, subtitle, steps[]})
  const newFrameworkStep = () => ({ label: "", title: "", description: "", examples: [] });
  const newFramework = () => ({ title: "", subtitle: "", steps: [newFrameworkStep()] });

  const updateFrameworkField = (fi, key, value) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      frameworks[fi] = { ...frameworks[fi], [key]: value };
      return { ...d, frameworks };
    });
  const addFramework = () =>
    setDraft((d) => ({ ...d, frameworks: [...(d.frameworks || []), newFramework()] }));
  const removeFramework = (fi) =>
    setDraft((d) => ({ ...d, frameworks: d.frameworks.filter((_, i) => i !== fi) }));
  const updateFrameworkStep = (fi, si, key, value) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      const steps = [...(frameworks[fi].steps || [])];
      steps[si] = { ...steps[si], [key]: value };
      frameworks[fi] = { ...frameworks[fi], steps };
      return { ...d, frameworks };
    });
  const addFrameworkStep = (fi) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      const steps = [...(frameworks[fi].steps || []), newFrameworkStep()];
      frameworks[fi] = { ...frameworks[fi], steps };
      return { ...d, frameworks };
    });
  const removeFrameworkStep = (fi, si) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      const steps = (frameworks[fi].steps || []).filter((_, i) => i !== si);
      frameworks[fi] = { ...frameworks[fi], steps };
      return { ...d, frameworks };
    });
  const updateFrameworkExample = (fi, si, ei, value) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      const steps = [...(frameworks[fi].steps || [])];
      const examples = [...(steps[si].examples || [])];
      examples[ei] = value;
      steps[si] = { ...steps[si], examples };
      frameworks[fi] = { ...frameworks[fi], steps };
      return { ...d, frameworks };
    });
  const addFrameworkExample = (fi, si) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      const steps = [...(frameworks[fi].steps || [])];
      steps[si] = { ...steps[si], examples: [...(steps[si].examples || []), ""] };
      frameworks[fi] = { ...frameworks[fi], steps };
      return { ...d, frameworks };
    });
  const removeFrameworkExample = (fi, si, ei) =>
    setDraft((d) => {
      const frameworks = [...(d.frameworks || [])];
      const steps = [...(frameworks[fi].steps || [])];
      steps[si] = {
        ...steps[si],
        examples: (steps[si].examples || []).filter((_, i) => i !== ei),
      };
      frameworks[fi] = { ...frameworks[fi], steps };
      return { ...d, frameworks };
    });

  const hasUndo = draft && undoMap[draft.slug];

  return (
    <div className="App" data-testid="admin-page">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-24">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] mb-3" style={{ color: "var(--mme-teal)" }}>
              Admin
            </p>
            <h1 className="h-display text-4xl md:text-5xl">Behind the kit.</h1>
          </div>
          <button onClick={loadAll} className="btn-secondary" data-testid="admin-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <Tabs defaultValue="scenarios" className="w-full">
          <TabsList className="mb-10" data-testid="admin-tabs">
            <TabsTrigger value="scenarios" data-testid="tab-scenarios">Scenarios ({scenarios.length})</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers ({customers.length})</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions ({transactions.length})</TabsTrigger>
            <TabsTrigger value="comp-access" data-testid="tab-comp-access">Comp access ({compGrants.length})</TabsTrigger>
            <TabsTrigger value="email-log" data-testid="tab-email-log">Email log</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="ab-test" data-testid="tab-ab-test">A/B test</TabsTrigger>
          </TabsList>

          <TabsContent value="scenarios">
            <div className="grid md:grid-cols-12 gap-8 lg:gap-12">
              {/* Side nav: list of scenarios grouped by category */}
              <aside className="md:col-span-4 lg:col-span-3" data-testid="admin-side-nav">
                <nav className="md:sticky md:top-24 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto pr-2">
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.22em] mb-5 pb-4 border-b"
                    style={{ color: "var(--mek-text-3)", borderColor: "var(--mek-border)" }}
                  >
                    All scenarios
                  </p>
                  {grouped.map(([category, items], gi) => {
                    const collapsed = !!collapsedCategories[category];
                    return (
                    <div key={category} className={gi > 0 ? "mt-7" : ""}>
                      <button
                        onClick={() => toggleCategory(category)}
                        className="w-full flex items-center gap-2 mb-3 px-2"
                        aria-expanded={!collapsed}
                        data-testid={`admin-category-toggle-${category}`}
                      >
                        {collapsed ? (
                          <ChevronRight size={12} style={{ color: "var(--mme-teal)" }} />
                        ) : (
                          <ChevronDown size={12} style={{ color: "var(--mme-teal)" }} />
                        )}
                        <p
                          className="text-[10px] font-bold uppercase tracking-[0.2em] flex items-center gap-2 flex-1 text-left"
                          style={{ color: "var(--mme-teal)" }}
                        >
                          {category}
                          <span
                            className="text-[9px] font-medium normal-case tracking-normal"
                            style={{ color: "var(--mek-text-3)" }}
                          >
                            · {items.length}
                          </span>
                          {!collapsed && items.length > 1 && (
                            <span
                              className="text-[9px] font-medium normal-case tracking-normal"
                              style={{ color: "var(--mek-text-3)" }}
                              title="Drag scenarios to reorder within this category"
                            >
                              · drag to reorder
                            </span>
                          )}
                        </p>
                      </button>
                      {!collapsed && (
                      <div>
                        {items.map((s, i) => {
                          const active = s.slug === activeSlug;
                          const isDragging = draggingSlug === s.slug;
                          const isDragTarget =
                            dragOverSlug === s.slug &&
                            draggingSlug &&
                            draggingSlug !== s.slug &&
                            scenarios.find((x) => x.slug === draggingSlug)?.category === category;
                          return (
                            <React.Fragment key={s.slug}>
                              {i > 0 && (
                                <div className="h-px mx-3" style={{ background: "var(--mek-border)" }} />
                              )}
                              <div
                                draggable
                                onDragStart={(e) => {
                                  setDraggingSlug(s.slug);
                                  e.dataTransfer.effectAllowed = "move";
                                  try { e.dataTransfer.setData("text/plain", s.slug); } catch {}
                                }}
                                onDragEnd={() => {
                                  setDraggingSlug(null);
                                  setDragOverSlug(null);
                                }}
                                onDragOver={(e) => {
                                  // Only allow drop on same-category items.
                                  const dragging = scenarios.find((x) => x.slug === draggingSlug);
                                  if (dragging && dragging.category === category && dragging.slug !== s.slug) {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = "move";
                                    if (dragOverSlug !== s.slug) setDragOverSlug(s.slug);
                                  }
                                }}
                                onDragLeave={() => {
                                  if (dragOverSlug === s.slug) setDragOverSlug(null);
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  const from = draggingSlug;
                                  setDraggingSlug(null);
                                  setDragOverSlug(null);
                                  if (from) reorderWithinCategory(from, s.slug);
                                }}
                                className="flex items-stretch rounded-md transition-colors group"
                                style={{
                                  background: active
                                    ? "var(--mme-teal)"
                                    : isDragTarget
                                    ? "rgba(42, 110, 115, 0.10)"
                                    : "transparent",
                                  opacity: isDragging ? 0.4 : 1,
                                  borderTop: isDragTarget ? "2px solid var(--mme-teal)" : "2px solid transparent",
                                }}
                                data-testid={`admin-scenario-row-${s.slug}`}
                              >
                                <span
                                  className="flex items-center pl-2 pr-1 cursor-grab active:cursor-grabbing"
                                  style={{
                                    color: active ? "rgba(255,255,255,0.7)" : "var(--mek-text-3)",
                                  }}
                                  title="Drag to reorder"
                                  data-testid={`admin-drag-handle-${s.slug}`}
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <GripVertical size={14} />
                                </span>
                                <button
                                  onClick={() => openScenario(s.slug)}
                                  className="flex-1 text-left block py-3 pr-3 rounded-md"
                                  style={{
                                    color: active ? "#ffffff" : "var(--mek-text)",
                                  }}
                                  data-testid={`admin-scenario-${s.slug}`}
                                >
                                  <span className="text-sm leading-snug font-medium">{s.title}</span>
                                </button>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                      )}
                    </div>
                  );})}
                </nav>
              </aside>

              {/* Editor */}
              <div className="md:col-span-8 lg:col-span-9">
                {!draft ? (
                  <div
                    className="card-soft p-10 md:p-16 text-center"
                    style={{ borderStyle: "dashed", borderColor: "var(--mek-border)" }}
                    data-testid="admin-empty-state"
                  >
                    <p className="font-serif text-2xl mb-3" style={{ color: "var(--mek-text)" }}>
                      Pick a scenario to edit.
                    </p>
                    <p className="text-prose text-sm">
                      Choose any scenario from the left to open its editor. Changes save back to the live library immediately, and you can undo your last save from the toast.
                    </p>
                  </div>
                ) : (
                  <div className="card-soft p-7 md:p-10" data-testid="admin-editor">
                    {/* Editor header */}
                    <div className="flex items-start justify-between mb-8 pb-5 border-b" style={{ borderColor: "var(--mek-border)" }}>
                      <div>
                        <p
                          className="text-[10px] font-bold uppercase tracking-[0.22em] mb-2"
                          style={{ color: "var(--mme-teal)" }}
                        >
                          Editing
                        </p>
                        <h2 className="font-serif text-2xl md:text-3xl leading-tight" style={{ color: "var(--mek-text)" }}>
                          {draft.title}
                        </h2>
                      </div>
                      {hasUndo && (
                        <button
                          onClick={() => undoLastSave(draft.slug)}
                          className="text-sm flex items-center gap-2 hover:underline shrink-0 mt-2"
                          style={{ color: "var(--mme-teal)" }}
                          data-testid="admin-undo"
                          title="Restore the version before your last save"
                        >
                          <Undo2 size={14} /> Undo last save
                        </button>
                      )}
                    </div>

                    {/* Short fields */}
                    <div className="space-y-7">
                      {SHORT_FIELDS.map(([f, label]) => (
                        <div key={f}>
                          <FieldLabel>{label}</FieldLabel>
                          {f === "category" ? (
                            <select
                              className="input-line"
                              value={draft[f] || ""}
                              onChange={(e) => updateField(f, e.target.value)}
                              data-testid={`field-${f}`}
                            >
                              <option value="" disabled>Select a category…</option>
                              {CATEGORY_ORDER.map((c) => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="input-line"
                              value={draft[f] || ""}
                              onChange={(e) => updateField(f, e.target.value)}
                              data-testid={`field-${f}`}
                            />
                          )}
                        </div>
                      ))}

                      {/* Textarea fields */}
                      {TEXTAREA_FIELDS.map(([f, label]) => (
                        <div key={f}>
                          <FieldLabel>{label}</FieldLabel>
                          <textarea
                            className="w-full p-4 rounded-md border bg-transparent text-prose leading-relaxed"
                            style={{ borderColor: "var(--mek-border)", minHeight: 130 }}
                            value={draft[f] || ""}
                            onChange={(e) => updateField(f, e.target.value)}
                            data-testid={`field-${f}`}
                          />
                        </div>
                      ))}

                      {/* List fields */}
                      {LIST_FIELDS.map(([f, label]) => (
                        <div key={f} className="pt-2">
                          <FieldLabel>{label}</FieldLabel>
                          <div className="space-y-2">
                            {(draft[f] || []).map((v, i) => {
                              const isTip = /^\s*pro[\s-]*tip\s*[:\-—]/i.test(v || "");
                              return (
                              <div
                                key={i}
                                className="flex gap-2 group items-start py-1"
                              >
                                <span className="text-xs font-mono pt-3 shrink-0 w-5" style={{ color: "var(--mek-text-3)" }}>
                                  {String(i + 1).padStart(2, "0")}
                                </span>
                                <input
                                  className="input-line flex-1"
                                  value={v}
                                  onChange={(e) => updateListField(f, i, e.target.value)}
                                  data-testid={`field-${f}-${i}`}
                                  style={isTip ? { background: "rgba(217, 184, 106, 0.10)", borderColor: "rgba(217, 184, 106, 0.55)" } : undefined}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleProTip(f, i)}
                                  className="text-xs mt-2 shrink-0 p-1.5 rounded transition-opacity"
                                  style={{
                                    color: isTip ? "var(--mme-gold)" : "var(--mek-text-3)",
                                    opacity: isTip ? 1 : 0.4,
                                  }}
                                  title={isTip ? "Remove Pro tip styling" : "Mark this as a Pro tip"}
                                  data-testid={`toggle-protip-${f}-${i}`}
                                >
                                  <Lightbulb size={14} fill={isTip ? "currentColor" : "none"} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeListItem(f, i)}
                                  className="text-xs opacity-40 hover:opacity-100 mt-2.5 shrink-0 p-1"
                                  style={{ color: "var(--mme-plum)" }}
                                  title="Remove this line"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => addListItem(f)}
                              className="text-sm flex items-center gap-1.5 mt-3 ml-7"
                              style={{ color: "var(--mme-teal)" }}
                            >
                              <Plus size={14} /> Add line
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Grouped fields (helpful_language + follow_through) */}
                      {GROUPED_FIELDS.map(([f, label]) => (
                        <div key={f} className="pt-4">
                          <FieldLabel>{label}</FieldLabel>
                          <p className="text-xs text-prose mb-4 -mt-1">
                            Organize this section into labeled groups (e.g. "Opening the conversation", "Within 24 hours").
                          </p>
                          <div className="space-y-5">
                            {(draft[f] || []).map((group, gi) => (
                              <div
                                key={gi}
                                className="border rounded-lg p-4"
                                style={{ borderColor: "var(--mek-border)", background: "rgba(245, 239, 227, 0.35)" }}
                                data-testid={`group-${f}-${gi}`}
                              >
                                <div className="flex gap-2 items-start mb-3">
                                  <input
                                    className="input-line flex-1"
                                    placeholder="Group label (e.g. Opening the conversation)"
                                    value={group.label || ""}
                                    onChange={(e) => updateGroupLabel(f, gi, e.target.value)}
                                    data-testid={`group-label-${f}-${gi}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeGroup(f, gi)}
                                    className="text-xs opacity-50 hover:opacity-100 mt-2.5 shrink-0 p-1"
                                    style={{ color: "var(--mme-plum)" }}
                                    title="Remove this group"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                <div className="space-y-2 ml-2">
                                  {(group.items || []).map((it, ii) => (
                                    <div key={ii} className="flex gap-2 items-start py-1">
                                      <span className="text-xs font-mono pt-3 shrink-0 w-5" style={{ color: "var(--mek-text-3)" }}>
                                        {String(ii + 1).padStart(2, "0")}
                                      </span>
                                      <input
                                        className="input-line flex-1"
                                        value={it}
                                        onChange={(e) => updateGroupItem(f, gi, ii, e.target.value)}
                                        data-testid={`group-item-${f}-${gi}-${ii}`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeGroupItem(f, gi, ii)}
                                        className="text-xs opacity-40 hover:opacity-100 mt-2.5 shrink-0 p-1"
                                        style={{ color: "var(--mme-plum)" }}
                                        title="Remove this line"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => addGroupItem(f, gi)}
                                    className="text-sm flex items-center gap-1.5 mt-2 ml-7"
                                    style={{ color: "var(--mme-teal)" }}
                                  >
                                    <Plus size={14} /> Add line
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addGroup(f)}
                              className="text-sm flex items-center gap-1.5 mt-1"
                              style={{ color: "var(--mme-teal)" }}
                              data-testid={`add-group-${f}`}
                            >
                              <Plus size={14} /> Add a group
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Frameworks editor (unified C.L.E.A.R. / boundary / any custom multi-step model) */}
                      <div className="pt-4">
                        <FieldLabel>Frameworks (optional multi-step models for this scenario)</FieldLabel>
                        <p className="text-xs text-prose mb-4 -mt-1">
                          Add a named, ordered framework — e.g. the C.L.E.A.R. feedback model, "How to set a boundary", or any custom model you want to teach in this scenario. Each step has a short label (letter or number), a title, a description, and optional example phrases.
                        </p>
                        <div className="space-y-7">
                          {(draft.frameworks || []).map((fw, fi) => (
                            <div
                              key={fi}
                              className="border rounded-lg p-5"
                              style={{ borderColor: "var(--mek-border)", background: "rgba(245, 239, 227, 0.45)" }}
                              data-testid={`framework-editor-${fi}`}
                            >
                              <div className="flex justify-between items-start mb-4">
                                <p
                                  className="text-[10px] font-bold uppercase tracking-[0.22em]"
                                  style={{ color: "var(--mme-teal)" }}
                                >
                                  Framework {fi + 1}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => removeFramework(fi)}
                                  className="text-xs opacity-60 hover:opacity-100 flex items-center gap-1"
                                  style={{ color: "var(--mme-plum)" }}
                                  title="Remove this framework"
                                  data-testid={`remove-framework-${fi}`}
                                >
                                  <X size={14} /> Remove
                                </button>
                              </div>

                              <div className="mb-3">
                                <FieldLabel>Framework title</FieldLabel>
                                <input
                                  className="input-line"
                                  placeholder="e.g. The C.L.E.A.R. model — a framework for hard feedback conversations"
                                  value={fw.title || ""}
                                  onChange={(e) => updateFrameworkField(fi, "title", e.target.value)}
                                  data-testid={`framework-title-${fi}`}
                                />
                              </div>
                              <div className="mb-5">
                                <FieldLabel>Subtitle (optional intro paragraph)</FieldLabel>
                                <textarea
                                  className="w-full p-3 rounded-md border bg-transparent text-prose leading-relaxed"
                                  style={{ borderColor: "var(--mek-border)", minHeight: 80 }}
                                  placeholder="A short italic intro shown above the steps."
                                  value={fw.subtitle || ""}
                                  onChange={(e) => updateFrameworkField(fi, "subtitle", e.target.value)}
                                  data-testid={`framework-subtitle-${fi}`}
                                />
                              </div>

                              <FieldLabel>Steps</FieldLabel>
                              <div className="space-y-4 ml-1">
                                {(fw.steps || []).map((step, si) => (
                                  <div
                                    key={si}
                                    className="border rounded-md p-4 bg-white"
                                    style={{ borderColor: "var(--mek-border)" }}
                                    data-testid={`framework-${fi}-step-editor-${si}`}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <p
                                        className="text-[10px] font-bold uppercase tracking-[0.22em]"
                                        style={{ color: "var(--mek-text-3)" }}
                                      >
                                        Step {si + 1}
                                      </p>
                                      <button
                                        type="button"
                                        onClick={() => removeFrameworkStep(fi, si)}
                                        className="text-xs opacity-50 hover:opacity-100"
                                        style={{ color: "var(--mme-plum)" }}
                                        title="Remove this step"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-3">
                                      <div className="sm:col-span-3">
                                        <FieldLabel>Label</FieldLabel>
                                        <input
                                          className="input-line"
                                          placeholder='e.g. "C" or "1"'
                                          value={step.label || ""}
                                          onChange={(e) => updateFrameworkStep(fi, si, "label", e.target.value)}
                                          data-testid={`framework-${fi}-step-${si}-label`}
                                        />
                                      </div>
                                      <div className="sm:col-span-9">
                                        <FieldLabel>Title</FieldLabel>
                                        <input
                                          className="input-line"
                                          placeholder="The step heading"
                                          value={step.title || ""}
                                          onChange={(e) => updateFrameworkStep(fi, si, "title", e.target.value)}
                                          data-testid={`framework-${fi}-step-${si}-title`}
                                        />
                                      </div>
                                    </div>
                                    <div className="mb-3">
                                      <FieldLabel>Description</FieldLabel>
                                      <textarea
                                        className="w-full p-3 rounded-md border bg-transparent text-prose leading-relaxed"
                                        style={{ borderColor: "var(--mek-border)", minHeight: 100 }}
                                        placeholder="Body text. Use blank lines to separate paragraphs."
                                        value={step.description || ""}
                                        onChange={(e) => updateFrameworkStep(fi, si, "description", e.target.value)}
                                        data-testid={`framework-${fi}-step-${si}-description`}
                                      />
                                    </div>
                                    <FieldLabel>Examples (quoted scripts or plain phrases)</FieldLabel>
                                    <div className="space-y-2">
                                      {(step.examples || []).map((ex, ei) => (
                                        <div key={ei} className="flex gap-2 items-start py-1">
                                          <span className="text-xs font-mono pt-3 shrink-0 w-5" style={{ color: "var(--mek-text-3)" }}>
                                            {String(ei + 1).padStart(2, "0")}
                                          </span>
                                          <input
                                            className="input-line flex-1"
                                            value={ex}
                                            onChange={(e) => updateFrameworkExample(fi, si, ei, e.target.value)}
                                            data-testid={`framework-${fi}-step-${si}-example-${ei}`}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => removeFrameworkExample(fi, si, ei)}
                                            className="text-xs opacity-40 hover:opacity-100 mt-2.5 shrink-0 p-1"
                                            style={{ color: "var(--mme-plum)" }}
                                          >
                                            <X size={14} />
                                          </button>
                                        </div>
                                      ))}
                                      <button
                                        type="button"
                                        onClick={() => addFrameworkExample(fi, si)}
                                        className="text-sm flex items-center gap-1.5 mt-1 ml-7"
                                        style={{ color: "var(--mme-teal)" }}
                                      >
                                        <Plus size={14} /> Add example
                                      </button>
                                    </div>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => addFrameworkStep(fi)}
                                  className="text-sm flex items-center gap-1.5 mt-2"
                                  style={{ color: "var(--mme-teal)" }}
                                  data-testid={`add-framework-step-${fi}`}
                                >
                                  <Plus size={14} /> Add step
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={addFramework}
                            className="text-sm flex items-center gap-1.5 mt-1"
                            style={{ color: "var(--mme-teal)" }}
                            data-testid="add-framework"
                          >
                            <Plus size={14} /> Add a framework
                          </button>
                        </div>
                      </div>

                      {/* See-also cross-link picker */}
                      <div className="pt-4">
                        <FieldLabel>"See also" — related scenarios (shown at the bottom of the page)</FieldLabel>
                        <p className="text-xs text-prose mb-4 -mt-1">
                          Pick other scenarios a reader should explore next. Cards appear at the bottom of the page in the order you add them.
                        </p>
                        <SeeAlsoPicker
                          value={draft.see_also || []}
                          options={scenarios}
                          currentSlug={draft.slug}
                          onChange={(next) => setDraft((d) => ({ ...d, see_also: next }))}
                        />
                      </div>

                      {/* Free-form "Additional content" — markdown */}
                      <div className="pt-4">
                        <FieldLabel>Additional content (optional)</FieldLabel>
                        <p className="text-xs text-prose mb-3 -mt-1">
                          Use this for one-off content that doesn't fit the standard sections — extra resources, alternative framings, deeper dives, "additional practical ways to support…" lists, and so on. Renders beneath the Manager Reminder.
                        </p>
                        <details
                          className="mb-3 text-xs"
                          style={{ color: "var(--mek-text-3)" }}
                        >
                          <summary className="cursor-pointer">Formatting cheatsheet</summary>
                          <pre
                            className="mt-2 p-3 rounded text-[11px] leading-relaxed whitespace-pre-wrap font-mono"
                            style={{ background: "rgba(245, 239, 227, 0.6)", border: "1px solid var(--mek-border)" }}
                          >
{`## Section heading
### Smaller heading
**bold text**
- Bullet item
- Another bullet

Plain paragraphs work too. Leave a blank line between blocks.`}
                          </pre>
                        </details>
                        <textarea
                          value={draft.extra_content || ""}
                          onChange={(e) => setDraft((d) => ({ ...d, extra_content: e.target.value }))}
                          placeholder="## Additional practical ways to support team morale&#10;&#10;Low morale rarely improves through motivational speeches alone.&#10;&#10;### Team building&#10;- Team lunches or coffee chats&#10;- Celebrating milestones together"
                          rows={14}
                          className="w-full p-4 text-sm rounded-md border bg-white font-mono leading-relaxed focus:outline-none"
                          style={{ borderColor: "var(--mek-border)" }}
                          data-testid="field-extra-content"
                        />
                      </div>

                    </div>

                    {/* Save bar */}
                    <div
                      className="mt-10 pt-6 border-t flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center"
                      style={{ borderColor: "var(--mek-border)" }}
                    >
                      <p className="text-xs text-prose">
                        Live on the public library the moment you save.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={() => openScenario(draft.slug)}
                          className="btn-secondary"
                          title="Discard your unsaved changes and reload from the library"
                        >
                          Discard changes
                        </button>
                        <button onClick={saveDraft} disabled={saving} className="btn-primary" data-testid="admin-save">
                          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="customers">
            {pendingAnnouncements.pending.length > 0 && (
              <div
                className="mb-5 px-5 py-5 rounded-lg"
                style={{
                  background: "#FBF6E8",
                  border: "1px solid var(--mme-gold-deep, #C9A95A)",
                }}
                data-testid="announcement-banner"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2"
                      style={{ color: "var(--mme-gold-deep, #8A6B1F)" }}
                    >
                      Ready to announce
                    </p>
                    <p className="text-base leading-snug" style={{ color: "var(--mek-text)" }}>
                      <span className="font-semibold">
                        {pendingAnnouncements.pending.length} new scenario
                        {pendingAnnouncements.pending.length === 1 ? "" : "s"}
                      </span>{" "}
                      <span style={{ color: "var(--mek-text-2)" }}>
                        haven't been announced to your{" "}
                        <span className="font-semibold">{pendingAnnouncements.recipient_count}</span>{" "}
                        subscribed customer
                        {pendingAnnouncements.recipient_count === 1 ? "" : "s"} yet.
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={previewAnnouncementEmail}
                      disabled={previewLoading}
                      className="text-sm px-3 py-2 rounded-full transition-opacity hover:opacity-80 disabled:opacity-50"
                      style={{
                        background: "transparent",
                        color: "var(--mme-teal)",
                        border: "1px solid var(--mme-teal)",
                      }}
                      data-testid="preview-announcement-btn"
                      title="See exactly what customers will receive"
                    >
                      {previewLoading ? "Loading…" : "Preview email"}
                    </button>
                    <button
                      onClick={markAllAnnounced}
                      className="text-sm px-3 py-2 rounded-full transition-opacity hover:opacity-80"
                      style={{
                        background: "transparent",
                        color: "var(--mek-text-2)",
                        border: "1px solid var(--mek-border)",
                      }}
                      data-testid="mark-announced-btn"
                      title="Mark as announced without sending an email"
                    >
                      Skip (mark announced)
                    </button>
                    <button
                      onClick={announceNewScenarios}
                      disabled={announcing}
                      className="text-sm font-medium px-4 py-2 rounded-full transition-opacity hover:opacity-90 disabled:opacity-50"
                      style={{ background: "var(--mme-teal)", color: "var(--mek-bg)" }}
                      data-testid="announce-scenarios-btn"
                    >
                      {announcing ? "Sending…" : "Send announcement email"}
                    </button>
                  </div>
                </div>
                <ul className="space-y-1.5 mt-3 pt-3" style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                  {pendingAnnouncements.pending.slice(0, 8).map((s) => (
                    <li
                      key={s.slug}
                      className="text-sm flex gap-3"
                      style={{ color: "var(--mek-text-2)" }}
                      data-testid={`pending-scenario-${s.slug}`}
                    >
                      <span aria-hidden="true" style={{ color: "var(--mme-gold-deep, #8A6B1F)" }}>
                        ·
                      </span>
                      <span>
                        <span style={{ color: "var(--mek-text)" }}>{s.title}</span>
                        <span style={{ color: "var(--mek-text-3)" }}> — {s.category}</span>
                      </span>
                    </li>
                  ))}
                  {pendingAnnouncements.pending.length > 8 && (
                    <li className="text-sm italic" style={{ color: "var(--mek-text-3)" }}>
                      + {pendingAnnouncements.pending.length - 8} more
                    </li>
                  )}
                </ul>
              </div>
            )}
            <div
              className="mb-5 px-5 py-4 rounded-lg flex items-center justify-between gap-4 flex-wrap"
              style={{
                background: "var(--mek-bg-2)",
                border: "1px solid var(--mek-border)",
              }}
              data-testid="purchase-notification-banner"
            >
              <div className="text-sm leading-snug" style={{ color: "var(--mek-text)" }}>
                <span className="font-semibold">Purchase alerts are on.</span>{" "}
                <span style={{ color: "var(--mek-text-2)" }}>
                  Every new sale fires an email to <code style={{ fontSize: "0.85em" }}>hello@managemeantexcellence.com</code>.
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={previewReceiptPdf}
                  className="text-sm px-3 py-2 rounded-full transition-opacity hover:opacity-80"
                  style={{
                    background: "transparent",
                    color: "var(--mme-teal)",
                    border: "1px solid var(--mme-teal)",
                  }}
                  data-testid="preview-receipt-pdf"
                  title="Open a sample receipt PDF in a new tab (what buyers download)"
                >
                  Preview receipt PDF
                </button>
                <button
                  onClick={sendTestBuyerEmail}
                  className="text-sm px-3 py-2 rounded-full transition-opacity hover:opacity-80"
                  style={{
                    background: "transparent",
                    color: "var(--mme-teal)",
                    border: "1px solid var(--mme-teal)",
                  }}
                  data-testid="send-test-buyer-email"
                  title="Sends the buyer-facing welcome + receipt email (what real customers see on purchase)"
                >
                  Preview buyer email
                </button>
                <button
                  onClick={sendTestPurchaseNotification}
                  className="text-sm font-medium px-4 py-2 rounded-full transition-opacity hover:opacity-80"
                  style={{ background: "var(--mme-teal)", color: "var(--mek-bg)" }}
                  data-testid="send-test-purchase-notification"
                  title="Sends the internal admin alert (the one you receive on each sale)"
                >
                  Send test alert
                </button>
              </div>
            </div>
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--mek-bg-2)" }}>
                  <tr>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Email</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>First purchase</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Last access</th>
                    <th className="text-right p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 && (
                    <tr><td colSpan={4} className="p-6 text-prose">No customers yet.</td></tr>
                  )}
                  {customers.map((c) => (
                    <tr key={c.email} className="border-t" style={{ borderColor: "var(--mek-border)" }} data-testid={`customer-${c.email}`}>
                      <td className="p-4 font-medium">{c.email}</td>
                      <td className="p-4 text-prose">{c.created_at?.slice(0, 10)}</td>
                      <td className="p-4 text-prose">{c.last_purchase_at?.slice(0, 10) || "—"}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => resendLink(c.email)} className="text-sm flex items-center gap-1 ml-auto" style={{ color: "var(--mme-teal)" }} data-testid={`resend-${c.email}`}>
                          <Send size={12} /> Resend link
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="transactions">
            {(() => {
              // Only paid gift transactions can meaningfully be "delivered" —
              // INITIATED rows are abandoned checkouts where no money changed
              // hands, so counting them as "sent gifts" was misleading.
              const paidGifts = transactions.filter(
                (t) => t.gift_to_email && t.payment_status === "paid"
              );
              const giftCount = paidGifts.length;
              const giftFulfilledCount = paidGifts.filter((t) => t.gift_fulfilled).length;
              const abandonedGifts = transactions.filter(
                (t) => t.gift_to_email && t.payment_status !== "paid"
              ).length;
              return giftCount > 0 || abandonedGifts > 0 ? (
                <div
                  className="mb-5 px-5 py-4 rounded-lg flex items-center gap-3 flex-wrap"
                  style={{
                    background: "var(--mme-gold)",
                    border: "1px solid var(--mme-gold-deep)",
                    color: "var(--mek-text)",
                  }}
                  data-testid="gift-stats-banner"
                >
                  <span aria-hidden="true" style={{ color: "var(--mme-gold-deep)" }}>✦</span>
                  <p className="text-sm leading-snug">
                    <span className="font-semibold">{giftCount}</span> paid gift{giftCount === 1 ? "" : "s"} —{" "}
                    <span className="font-semibold">{giftFulfilledCount}</span> delivered.
                    {abandonedGifts > 0 && (
                      <span style={{ color: "var(--mek-text-2)" }}>
                        {" "}({abandonedGifts} abandoned cart{abandonedGifts === 1 ? "" : "s"} — never paid.)
                      </span>
                    )}
                  </p>
                </div>
              ) : null;
            })()}
            <div className="card-soft overflow-hidden">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--mek-bg-2)" }}>
                  <tr>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Email</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Amount</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Status</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Gift</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Created</th>
                    <th className="text-left p-4 text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-2)" }}>Fulfilled</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-prose">No transactions yet.</td></tr>
                  )}
                  {transactions.map((t) => (
                    <tr
                      key={t.session_id}
                      className="border-t"
                      style={{
                        borderColor: "var(--mek-border)",
                        background: t.gift_to_email ? "rgba(244, 212, 146, 0.10)" : "transparent",
                      }}
                      data-testid={`transaction-row-${t.session_id}`}
                    >
                      <td className="p-4">{t.email}</td>
                      <td className="p-4">${t.amount?.toFixed(2)} {t.currency}</td>
                      <td className="p-4">
                        <span className={`pill ${t.payment_status === "paid" ? "pill-sage" : "pill-stone"}`}>{t.payment_status}</span>
                      </td>
                      <td className="p-4">
                        {t.gift_to_email ? (
                          <div className="flex flex-col gap-1" data-testid="gift-cell">
                            <span className="flex items-center gap-1.5 text-[12px]" style={{ color: "var(--mme-gold-deep)" }}>
                              <span aria-hidden="true">✦</span>
                              <span className="font-semibold">{t.gift_to_email}</span>
                            </span>
                            {t.sender_name && (
                              <span className="text-[11px]" style={{ color: "var(--mek-text-3)" }}>
                                from {t.sender_name}
                              </span>
                            )}
                            {(() => {
                              // For unpaid transactions, the gift was never
                              // "pending delivery" — it was an abandoned cart.
                              // Labelling it as "Pending" is misleading and
                              // makes Courtney think she has work to do.
                              const unpaid = t.payment_status !== "paid";
                              const failed = t.gift_delivery_failed && !t.gift_fulfilled;
                              let label, pillClass;
                              if (unpaid) {
                                label = "Not paid";
                                pillClass = "pill-stone";
                              } else if (failed) {
                                label = "Delivery failed";
                                pillClass = "pill-terracotta";
                              } else if (t.gift_fulfilled) {
                                label = "Delivered";
                                pillClass = "pill-sage";
                              } else {
                                label = "Pending";
                                pillClass = "pill-stone";
                              }
                              return (
                                <span className={`pill ${pillClass} text-[10px] self-start mt-0.5`}>
                                  {label}
                                </span>
                              );
                            })()}
                            {t.payment_status === "paid" && (
                              <button
                                onClick={() => resendGift(t.session_id, t.gift_to_email)}
                                className="text-[11px] underline self-start mt-1 hover:opacity-70"
                                style={{ color: "var(--mme-teal)" }}
                                data-testid={`resend-gift-${t.session_id}`}
                                title={`Re-send the gift email to ${t.gift_to_email}`}
                              >
                                Resend gift email
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-[12px]" style={{ color: "var(--mek-text-3)" }}>—</span>
                        )}
                      </td>
                      <td className="p-4 text-prose">{t.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="p-4">{t.fulfilled ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="comp-access">
            <CompAccessPanel
              grants={compGrants}
              onChange={setCompGrants}
            />
          </TabsContent>

          <TabsContent value="email-log">
            <EmailLogPanel />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsPanel
              data={analytics}
              days={analyticsDays}
              onDaysChange={async (d) => {
                setAnalyticsDays(d);
                try {
                  const { data } = await api.get(`/admin/analytics?days=${d}`);
                  setAnalytics(data);
                } catch (_) {
                  toast.error("Couldn't refresh analytics.");
                }
              }}
            />
          </TabsContent>
          <TabsContent value="ab-test">
            <ABTestPanel />
          </TabsContent>
        </Tabs>
      </section>
      {previewHtml !== null && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          style={{ background: "rgba(26, 42, 44, 0.55)" }}
          onClick={() => setPreviewHtml(null)}
          data-testid="preview-announcement-modal"
        >
          <div
            className="relative w-full max-w-2xl rounded-xl overflow-hidden flex flex-col"
            style={{
              background: "var(--mek-bg)",
              border: "1px solid var(--mek-border)",
              maxHeight: "92vh",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: "var(--mek-border)" }}
            >
              <div>
                <p
                  className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1"
                  style={{ color: "var(--mek-text-2)" }}
                >
                  Announcement email · preview
                </p>
                <p className="text-sm" style={{ color: "var(--mek-text)" }}>
                  This is exactly what subscribed customers will receive.
                </p>
              </div>
              <button
                onClick={() => setPreviewHtml(null)}
                className="text-sm px-3 py-1.5 rounded-full hover:opacity-80"
                style={{
                  background: "var(--mek-bg-2)",
                  border: "1px solid var(--mek-border)",
                  color: "var(--mek-text-2)",
                }}
                data-testid="preview-close-btn"
              >
                Close
              </button>
            </div>
            <iframe
              title="Announcement email preview"
              srcDoc={previewHtml}
              className="flex-1 w-full"
              style={{ background: "#FDFBF7", border: 0, minHeight: "60vh" }}
              data-testid="preview-announcement-iframe"
            />
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}


/**
 * CompAccessPanel — grant and audit complimentary toolkit access.
 *
 * Use this for reviewers, beta testers, friends, or anyone who should get
 * the toolkit without going through Stripe checkout. Granting access:
 *   1. Upserts a customer record (flagged `source: "comp"`).
 *   2. Emails them a real magic-link, styled like a purchase delivery.
 *   3. Records the grant in `comp_grants` for an audit trail.
 *
 * Revoking access deletes the customer record and invalidates outstanding
 * links, but keeps the audit row so history is preserved.
 */
function CompAccessPanel({ grants, onChange }) {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const grant = async (e) => {
    e?.preventDefault?.();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned) return;
    setSubmitting(true);
    try {
      await api.post("/admin/comp-access", { email: cleaned, note: note.trim() || null });
      toast.success(`Access granted to ${cleaned}. They'll receive a magic-link email shortly.`);
      setEmail("");
      setNote("");
      const { data } = await api.get("/admin/comp-access");
      onChange(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't grant access. Check the email and try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (grantEmail) => {
    try {
      await api.post(`/admin/customers/${encodeURIComponent(grantEmail)}/resend-link`);
      toast.success(`New access link sent to ${grantEmail}.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't resend the link.";
      toast.error(msg);
    }
  };

  const revoke = async (grantEmail) => {
    if (!window.confirm(`Revoke complimentary access for ${grantEmail}? Their current link will stop working immediately.`)) {
      return;
    }
    try {
      await api.delete(`/admin/comp-access/${encodeURIComponent(grantEmail)}`);
      toast.success(`Access revoked for ${grantEmail}.`);
      const { data } = await api.get("/admin/comp-access");
      onChange(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't revoke access.";
      toast.error(msg);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch (_) { return iso; }
  };

  return (
    <div className="space-y-10" data-testid="comp-access-panel">
      {/* Grant form */}
      <section
        className="rounded-xl border p-6 md:p-8"
        style={{ borderColor: "var(--mek-border)", background: "rgba(245, 239, 227, 0.45)" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <Gift size={18} style={{ color: "var(--mme-teal)" }} />
          <h2 className="h-display text-2xl">Grant complimentary access</h2>
        </div>
        <p className="text-sm text-prose max-w-2xl mb-6">
          Send the toolkit to a reviewer, beta tester, or friend without a Stripe charge. They'll get a magic-link email that looks identical to a paid purchase delivery.
        </p>
        <form onSubmit={grant} className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--mek-text-2)" }}>
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="reviewer@example.com"
              className="w-full px-3 py-2.5 text-sm rounded-md border bg-white focus:outline-none"
              style={{ borderColor: "var(--mek-border)" }}
              data-testid="comp-email-input"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--mek-text-2)" }}>
              Note (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 240))}
              placeholder="Beta tester, friend, editor…"
              className="w-full px-3 py-2.5 text-sm rounded-md border bg-white focus:outline-none"
              style={{ borderColor: "var(--mek-border)" }}
              data-testid="comp-note-input"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed h-[42px]"
            data-testid="comp-grant-btn"
          >
            <Send size={14} />
            {submitting ? "Sending…" : "Grant access"}
          </button>
        </form>
      </section>

      {/* History */}
      <section>
        <h3 className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: "var(--mek-text-2)" }}>
          History ({grants.length})
        </h3>
        {grants.length === 0 ? (
          <p
            className="text-sm italic px-4 py-6 rounded-md border border-dashed text-center"
            style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-3)" }}
          >
            No complimentary grants yet. Use the form above to send one.
          </p>
        ) : (
          <div className="overflow-x-auto border rounded-lg" style={{ borderColor: "var(--mek-border)" }}>
            <table className="min-w-full text-sm" data-testid="comp-grants-table">
              <thead style={{ background: "rgba(245, 239, 227, 0.6)" }}>
                <tr>
                  <th className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.14em]">Email</th>
                  <th className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.14em]">Note</th>
                  <th className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.14em]">Granted</th>
                  <th className="text-left p-4 font-bold text-[11px] uppercase tracking-[0.14em]">By</th>
                  <th className="text-right p-4 font-bold text-[11px] uppercase tracking-[0.14em]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g, idx) => (
                  <tr
                    key={`${g.email}-${g.created_at || idx}`}
                    className="border-t"
                    style={{ borderColor: "var(--mek-border)" }}
                    data-testid={`comp-row-${g.email}`}
                  >
                    <td className="p-4 font-medium" style={{ color: "var(--mek-text)" }}>{g.email}</td>
                    <td className="p-4" style={{ color: "var(--mek-text-2)" }}>{g.note || <span className="italic" style={{ color: "var(--mek-text-3)" }}>—</span>}</td>
                    <td className="p-4 whitespace-nowrap" style={{ color: "var(--mek-text-2)" }}>{fmtDate(g.created_at)}</td>
                    <td className="p-4" style={{ color: "var(--mek-text-3)" }}>{g.granted_by || "—"}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => resend(g.email)}
                        className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white"
                        style={{ color: "var(--mme-teal)" }}
                        title="Send a fresh magic-link email"
                        data-testid={`comp-resend-${g.email}`}
                      >
                        <RefreshCw size={12} /> Resend
                      </button>
                      <button
                        type="button"
                        onClick={() => revoke(g.email)}
                        className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white ml-1"
                        style={{ color: "var(--mme-plum)" }}
                        title="Revoke access (invalidates current links)"
                        data-testid={`comp-revoke-${g.email}`}
                      >
                        <Trash2 size={12} /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}


/**
 * AnalyticsPanel — content-level dashboard built on the lightweight
 * `analytics_events` collection. Pairs with Google Analytics 4 (which gives
 * traffic/source data) to answer the "what content is working?" question.
 *
 * Top metrics:
 *   Landing views · Scenario views · PDF downloads · Checkout starts
 *   + landing → checkout conversion rate.
 *
 * Below: ranked list of top scenarios by view count, plus a tiny
 * sparkline-style view trend so you can see if a recent edit moved demand.
 */
function EmailLogPanel() {
  const [items, setItems] = useState(null);
  const [emailFilter, setEmailFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (emailFilter.trim()) params.set("email", emailFilter.trim().toLowerCase());
      if (kindFilter) params.set("kind", kindFilter);
      params.set("limit", "200");
      const { data } = await api.get(`/admin/email-log?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Couldn't load email log.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Load on mount + whenever filters change
  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kindFilter]);

  const statusPill = (status) => {
    const map = {
      sent: "pill-sage",
      failed: "pill-terracotta",
      mocked: "pill-stone",
      pending: "pill-stone",
    };
    return map[status] || "pill-stone";
  };

  const kinds = [
    { value: "", label: "All kinds" },
    { value: "gift", label: "Gift recipient" },
    { value: "gift_sender_confirmation", label: "Gift sender confirmation" },
    { value: "generic", label: "Other (alerts, magic links, announcements)" },
  ];

  return (
    <div className="space-y-5" data-testid="email-log-panel">
      <div
        className="px-5 py-4 rounded-lg"
        style={{ background: "var(--mek-bg-2)", border: "1px solid var(--mek-border)" }}
      >
        <p className="text-sm" style={{ color: "var(--mek-text)" }}>
          <span className="font-semibold">Every outbound email is logged here.</span>{" "}
          <span style={{ color: "var(--mek-text-2)" }}>
            Use this to confirm whether a gift recipient actually received their email — or whether Resend rejected it. Failed sends are flagged in coral.
          </span>
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={emailFilter}
          onChange={(e) => setEmailFilter(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Filter by recipient email…"
          className="px-3 py-2 rounded-md text-sm flex-1 min-w-[220px]"
          style={{ background: "var(--mek-bg)", border: "1px solid var(--mek-border)", color: "var(--mek-text)" }}
          data-testid="email-log-email-filter"
        />
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="px-3 py-2 rounded-md text-sm"
          style={{ background: "var(--mek-bg)", border: "1px solid var(--mek-border)", color: "var(--mek-text)" }}
          data-testid="email-log-kind-filter"
        >
          {kinds.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
        <button
          onClick={load}
          className="text-sm px-4 py-2 rounded-full font-medium"
          style={{ background: "var(--mme-teal)", color: "var(--mek-bg)" }}
          data-testid="email-log-refresh"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm" data-testid="email-log-table">
          <thead className="text-prose-quiet text-left text-xs uppercase tracking-wider">
            <tr style={{ borderBottom: "1px solid var(--mek-border)" }}>
              <th className="p-4">When</th>
              <th className="p-4">Recipient</th>
              <th className="p-4">Subject</th>
              <th className="p-4">Kind</th>
              <th className="p-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {items === null && (
              <tr>
                <td colSpan={5} className="p-6 text-prose italic">
                  Loading…
                </td>
              </tr>
            )}
            {items !== null && items.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-prose">
                  No emails logged{emailFilter || kindFilter ? " matching those filters" : ""}.
                </td>
              </tr>
            )}
            {items !== null &&
              items.map((it, idx) => (
                <tr
                  key={`${it.created_at}-${idx}`}
                  className="border-t"
                  style={{ borderColor: "var(--mek-border)" }}
                  data-testid={`email-log-row-${idx}`}
                >
                  <td className="p-4 text-prose text-xs whitespace-nowrap">
                    {(it.created_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-4">{it.to_email}</td>
                  <td
                    className="p-4 text-prose-quiet text-xs"
                    title={it.subject}
                    style={{ maxWidth: "320px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {it.subject}
                  </td>
                  <td className="p-4 text-prose text-xs">{it.kind || "generic"}</td>
                  <td className="p-4">
                    <span className={`pill ${statusPill(it.status)} text-[10px]`}>
                      {it.status || "—"}
                    </span>
                    {it.status === "failed" && it.error && (
                      <p
                        className="text-[11px] mt-1 italic"
                        style={{ color: "var(--mek-text-3)", maxWidth: "260px" }}
                        title={it.error}
                      >
                        {it.error.slice(0, 80)}
                        {it.error.length > 80 ? "…" : ""}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AnalyticsPanel({ data, days, onDaysChange }) {
  const [recoverySending, setRecoverySending] = useState(false);
  const [recoveryTesting, setRecoveryTesting] = useState(false);
  const [nurtureSending, setNurtureSending] = useState(false);
  const [nurtureTesting, setNurtureTesting] = useState(false);
  const [nurtureBackfillOpen, setNurtureBackfillOpen] = useState(false);
  const [nurtureBackfillDays, setNurtureBackfillDays] = useState(365);
  const [nurtureEligible, setNurtureEligible] = useState(null);

  if (!data) {
    return (
      <p className="text-sm italic" style={{ color: "var(--mek-text-3)" }}>
        Loading analytics…
      </p>
    );
  }

  const sendRecoveryNow = async () => {
    if (recoverySending) return;
    setRecoverySending(true);
    try {
      const { data: result } = await api.post("/admin/recovery-emails/send-now");
      if (result.sent > 0) {
        toast.success(`Sent ${result.sent} recovery email${result.sent === 1 ? "" : "s"}.`);
      } else if (result.skipped > 0) {
        toast.message(`No new nudges sent — ${result.skipped} cart${result.skipped === 1 ? "" : "s"} already handled.`);
      } else {
        toast.message("No eligible carts right now. Recovery emails are sent 2h after checkout.");
      }
    } catch (e) {
      toast.error("Couldn't run recovery — please try again.");
    } finally {
      setRecoverySending(false);
    }
  };

  const sendRecoveryTest = async () => {
    if (recoveryTesting) return;
    setRecoveryTesting(true);
    try {
      const { data: result } = await api.post("/admin/recovery-emails/test");
      if (result.ok) {
        toast.success(`Preview email sent to ${result.sent_to}.`);
      } else {
        toast.error("Couldn't send preview — check Resend config.");
      }
    } catch (e) {
      toast.error("Couldn't send test email.");
    } finally {
      setRecoveryTesting(false);
    }
  };

  // Preview the 2-day nurture email — sends the same HTML customers get
  // to NOTIFY_EMAIL. No transaction records touched.
  const sendNurtureTest = async () => {
    if (nurtureTesting) return;
    setNurtureTesting(true);
    try {
      const { data: result } = await api.post("/admin/nurture-emails/test");
      if (result.ok) {
        toast.success(`Preview email sent to ${result.sent_to}.`);
      } else {
        toast.error("Couldn't send preview — check Resend config.");
      }
    } catch (e) {
      toast.error("Couldn't send test email.");
    } finally {
      setNurtureTesting(false);
    }
  };

  // Open the backfill modal and pre-fetch the eligible count for the
  // default window so Courtney sees the blast size before firing.
  const openNurtureBackfill = async () => {
    setNurtureBackfillOpen(true);
    setNurtureEligible(null);
    try {
      const { data: result } = await api.get(
        `/admin/nurture-emails/eligible-count?max_age_days=${nurtureBackfillDays}`
      );
      setNurtureEligible(result.eligible);
    } catch (e) {
      setNurtureEligible(0);
    }
  };

  // Re-count whenever the operator changes the max-age window.
  const refreshNurtureEligible = async (dayCount) => {
    setNurtureEligible(null);
    try {
      const { data: result } = await api.get(
        `/admin/nurture-emails/eligible-count?max_age_days=${dayCount}`
      );
      setNurtureEligible(result.eligible);
    } catch (e) {
      setNurtureEligible(0);
    }
  };

  // Actually fire the backfill blast.
  const runNurtureBackfill = async () => {
    if (nurtureSending) return;
    setNurtureSending(true);
    try {
      const { data: result } = await api.post("/admin/nurture-emails/send-now", {
        max_age_days: nurtureBackfillDays,
      });
      if (result.sent > 0) {
        toast.success(`Sent ${result.sent} nurture email${result.sent === 1 ? "" : "s"}.`);
      } else if (result.skipped > 0) {
        toast.message(`No new sends — ${result.skipped} skipped (already handled or already bought).`);
      } else {
        toast.message("No eligible carts right now.");
      }
      setNurtureBackfillOpen(false);
    } catch (e) {
      toast.error("Couldn't run nurture — please try again.");
    } finally {
      setNurtureSending(false);
    }
  };

  const stat = (label, value, hint) => (
    <div
      className="rounded-lg border px-5 py-4 bg-white"
      style={{ borderColor: "var(--mek-border)" }}
    >
      <p
        className="text-[10px] font-bold uppercase tracking-[0.18em]"
        style={{ color: "var(--mek-text-3)" }}
      >
        {label}
      </p>
      <p
        className="text-3xl font-serif mt-1"
        style={{ color: "var(--mek-text)", fontFamily: '"Cormorant Garamond", Georgia, serif' }}
      >
        {value}
      </p>
      {hint && (
        <p className="text-xs mt-0.5" style={{ color: "var(--mek-text-3)" }}>
          {hint}
        </p>
      )}
    </div>
  );

  // Sparkline of scenario_view + landing_view over the trend window
  const trend = data.trend || [];
  const maxVal = Math.max(
    1,
    ...trend.map((d) => Math.max(d.landing_view || 0, d.scenario_view || 0)),
  );

  const maxScenarioViews = Math.max(1, ...((data.top_scenarios || []).map((s) => s.views)));

  return (
    <div className="space-y-10" data-testid="analytics-panel">
      {/* Time-window selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-prose">
          Showing data from the last <span className="font-semibold">{days}</span> day{days === 1 ? "" : "s"}.
        </p>
        <div className="flex gap-2">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDaysChange(d)}
              className="px-3 py-1.5 text-xs rounded-full border transition-colors"
              style={{
                borderColor: d === days ? "var(--mme-teal)" : "var(--mek-border)",
                background: d === days ? "var(--mme-teal)" : "transparent",
                color: d === days ? "white" : "var(--mek-text-2)",
              }}
              data-testid={`analytics-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Top-level stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stat("Landing views", data.totals.landing_view)}
        {stat("Scenario views", data.totals.scenario_view)}
        {stat("PDF downloads", data.totals.pdf_download + data.totals.toolkit_pdf_download)}
        {stat("Checkout starts", data.totals.checkout_started)}
        {stat(
          "Conversion",
          `${data.conversion_landing_to_checkout_pct}%`,
          "Landing → Checkout",
        )}
      </div>

      {/* Cart funnel (paid vs abandoned) — also renders when there's no
          activity in the window but there are pending recovery / nurture
          emails to send, so the operator can always reach those controls. */}
      {data.carts &&
        (data.carts.started > 0 ||
          data.carts.recovery?.pending > 0 ||
          data.carts.recovery?.sent > 0 ||
          data.carts.recovery?.recovered_lifetime > 0 ||
          data.carts.nurture?.pending > 0 ||
          data.carts.nurture?.sent > 0 ||
          data.carts.nurture?.backlog > 0) && (
        <section data-testid="cart-funnel">
          <h3
            className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3"
            style={{ color: "var(--mek-text-2)" }}
          >
            Cart funnel
          </h3>
          <div
            className="rounded-lg border p-6 bg-white"
            style={{ borderColor: "var(--mek-border)" }}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                  Started checkout
                </p>
                <p className="text-2xl font-serif mt-1" style={{ color: "var(--mek-text)" }}>
                  {data.carts.started}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "#4A613F" }}>
                  Completed
                </p>
                <p className="text-2xl font-serif mt-1" style={{ color: "var(--mek-text)" }}>
                  {data.carts.completed}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mme-terracotta, #8B3E3E)" }}>
                  Abandoned
                </p>
                <p className="text-2xl font-serif mt-1" style={{ color: "var(--mek-text)" }}>
                  {data.carts.abandoned}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                  Completion rate
                </p>
                <p className="text-2xl font-serif mt-1" style={{ color: "var(--mek-text)" }}>
                  {data.carts.completion_rate_pct}%
                </p>
              </div>
            </div>

            {/* Stacked horizontal bar — completed vs abandoned */}
            <div
              className="h-3 rounded-full overflow-hidden flex"
              style={{ background: "var(--mek-bg-2)" }}
              title={`${data.carts.completed} completed · ${data.carts.abandoned} abandoned`}
            >
              <div
                style={{
                  width: `${(data.carts.completed / Math.max(1, data.carts.started)) * 100}%`,
                  background: "#4A613F",
                }}
              />
              <div
                style={{
                  width: `${(data.carts.abandoned / Math.max(1, data.carts.started)) * 100}%`,
                  background: "var(--mme-terracotta, #8B3E3E)",
                  opacity: 0.85,
                }}
              />
            </div>

            {/* Self vs gift breakdown */}
            <div className="mt-5 grid grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                  Self purchases
                </p>
                <p className="mt-1" style={{ color: "var(--mek-text)" }}>
                  <span className="font-semibold">{data.carts.by_flow.self.completed}</span> completed ·{" "}
                  <span style={{ color: "var(--mek-text-2)" }}>{data.carts.by_flow.self.abandoned} abandoned</span>
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                  Gift purchases
                </p>
                <p className="mt-1" style={{ color: "var(--mek-text)" }}>
                  <span className="font-semibold">{data.carts.by_flow.gift.completed}</span> completed ·{" "}
                  <span style={{ color: "var(--mek-text-2)" }}>{data.carts.by_flow.gift.abandoned} abandoned</span>
                </p>
              </div>
            </div>

            {data.carts.abandoned > 0 && (
              <p
                className="text-xs mt-5 italic"
                style={{ color: "var(--mek-text-3)" }}
              >
                Abandoned carts are buyers who clicked Buy but didn&apos;t finish payment. Normal range is 50–70% completion for digital products.
              </p>
            )}

            {/* Recovery emails — auto-nudge for abandoned carts */}
            {data.carts.recovery && (
              <div
                className="mt-6 pt-5 border-t flex flex-wrap items-center justify-between gap-4"
                style={{ borderColor: "var(--mek-border)" }}
                data-testid="cart-recovery-block"
              >
                <div className="min-w-0">
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-2 flex-wrap"
                    style={{ color: "var(--mek-text-3)" }}
                  >
                    Recovery emails
                    {!data.carts.recovery.enabled && (
                      <span className="normal-case tracking-normal" style={{ color: "var(--mme-terracotta, #8B3E3E)" }}>
                        · disabled
                      </span>
                    )}
                    {data.carts.recovery.recovered_lifetime > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-[0.1em]"
                        style={{
                          background: "rgba(74, 97, 63, 0.12)",
                          color: "#4A613F",
                        }}
                        data-testid="recovered-lifetime-badge"
                        title="Buyers who completed checkout after a recovery email"
                      >
                        <span aria-hidden="true">●</span>
                        {data.carts.recovery.recovered_lifetime} RECOVERED · LIFETIME
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-sm" style={{ color: "var(--mek-text)" }}>
                    <span className="font-semibold" data-testid="recovery-sent-count">
                      {data.carts.recovery.sent}
                    </span>{" "}
                    sent in the last {days} day{days === 1 ? "" : "s"} ·{" "}
                    <span className="font-semibold" data-testid="recovery-pending-count">
                      {data.carts.recovery.pending}
                    </span>{" "}
                    waiting (nudged {data.carts.recovery.delay_hours}h after checkout)
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={sendRecoveryTest}
                    disabled={recoveryTesting || !data.carts.recovery.enabled}
                    data-testid="recovery-test-btn"
                    className="text-xs px-3 py-2 rounded-full border transition disabled:opacity-50"
                    style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-2)" }}
                  >
                    {recoveryTesting ? "Sending…" : "Preview email"}
                  </button>
                  <button
                    type="button"
                    onClick={sendRecoveryNow}
                    disabled={recoverySending || !data.carts.recovery.enabled}
                    data-testid="recovery-send-now-btn"
                    className="text-xs px-3 py-2 rounded-full transition disabled:opacity-50"
                    style={{ background: "var(--mek-text)", color: "var(--mek-bg)" }}
                  >
                    {recoverySending ? "Running…" : "Run recovery now"}
                  </button>
                </div>
              </div>
            )}

            {/* 2-day nurture emails — softer second touch */}
            {data.carts.nurture && (
              <div
                className="mt-4 pt-5 border-t flex flex-wrap items-center justify-between gap-4"
                style={{ borderColor: "var(--mek-border)" }}
                data-testid="cart-nurture-block"
              >
                <div className="min-w-0">
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-2 flex-wrap"
                    style={{ color: "var(--mek-text-3)" }}
                  >
                    2-day nurture email
                    {!data.carts.nurture.enabled && (
                      <span className="normal-case tracking-normal" style={{ color: "var(--mme-terracotta, #8B3E3E)" }}>
                        · disabled
                      </span>
                    )}
                    {data.carts.nurture.backlog > 0 && (
                      <span
                        className="normal-case tracking-normal"
                        style={{ color: "var(--mme-terracotta, #8B3E3E)" }}
                        title={`${data.carts.nurture.backlog} historical carts have never received the nurture email`}
                        data-testid="nurture-backlog-badge"
                      >
                        · {data.carts.nurture.backlog} in backlog
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-sm" style={{ color: "var(--mek-text)" }}>
                    <span className="font-semibold" data-testid="nurture-sent-count">
                      {data.carts.nurture.sent}
                    </span>{" "}
                    sent in the last {days} day{days === 1 ? "" : "s"} ·{" "}
                    <span className="font-semibold" data-testid="nurture-pending-count">
                      {data.carts.nurture.pending}
                    </span>{" "}
                    waiting (sent {data.carts.nurture.delay_days} days after checkout, max {data.carts.nurture.max_age_days} days back)
                  </p>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  <button
                    type="button"
                    onClick={sendNurtureTest}
                    disabled={nurtureTesting || !data.carts.nurture.enabled}
                    data-testid="nurture-test-btn"
                    className="text-xs px-3 py-2 rounded-full border transition disabled:opacity-50"
                    style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-2)" }}
                  >
                    {nurtureTesting ? "Sending…" : "Preview email"}
                  </button>
                  <button
                    type="button"
                    onClick={openNurtureBackfill}
                    disabled={!data.carts.nurture.enabled}
                    data-testid="nurture-backfill-btn"
                    className="text-xs px-3 py-2 rounded-full transition disabled:opacity-50"
                    style={{ background: "#087280", color: "#ffffff" }}
                  >
                    Send to backlog…
                  </button>
                </div>
              </div>
            )}

            {/* Backfill modal — inline dialog */}
            {nurtureBackfillOpen && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                style={{ background: "rgba(26, 42, 44, 0.6)" }}
                data-testid="nurture-backfill-modal"
                onClick={(e) => {
                  if (e.target === e.currentTarget && !nurtureSending) setNurtureBackfillOpen(false);
                }}
              >
                <div
                  className="rounded-lg bg-white max-w-md w-full p-8"
                  style={{ border: "1px solid var(--mek-border)" }}
                >
                  <h3 className="font-serif text-2xl mb-3" style={{ color: "var(--mek-text)" }}>
                    Send nurture to backlog
                  </h3>
                  <p className="text-sm mb-5" style={{ color: "var(--mek-text-2)" }}>
                    One-time blast to everyone who abandoned a cart in the last window below and never got the nurture email. Existing customers, admins, and duplicate emails are automatically skipped.
                  </p>

                  <label className="text-[10px] font-bold uppercase tracking-[0.16em] block" style={{ color: "var(--mek-text-3)" }}>
                    Reach back this many days
                  </label>
                  <input
                    type="number"
                    min="2"
                    max="720"
                    value={nurtureBackfillDays}
                    onChange={(e) => {
                      const v = Math.max(2, parseInt(e.target.value, 10) || 60);
                      setNurtureBackfillDays(v);
                    }}
                    onBlur={() => refreshNurtureEligible(nurtureBackfillDays)}
                    data-testid="nurture-backfill-days-input"
                    className="mt-1 w-full px-3 py-2 rounded border text-sm"
                    style={{ borderColor: "var(--mek-border)" }}
                  />

                  <p
                    className="mt-4 text-sm px-4 py-3 rounded"
                    style={{ background: "#F5F1E8", color: "var(--mek-text)" }}
                    data-testid="nurture-backfill-eligible-count"
                  >
                    {nurtureEligible === null
                      ? "Counting eligible carts…"
                      : nurtureEligible === 0
                      ? "No eligible carts in that window."
                      : `${nurtureEligible} cart${nurtureEligible === 1 ? "" : "s"} will receive an email.`}
                  </p>

                  <div className="mt-6 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setNurtureBackfillOpen(false)}
                      disabled={nurtureSending}
                      className="text-sm px-4 py-2 rounded-full border transition disabled:opacity-50"
                      style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-2)" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={runNurtureBackfill}
                      disabled={nurtureSending || !nurtureEligible}
                      data-testid="nurture-backfill-send-btn"
                      className="text-sm px-4 py-2 rounded-full transition disabled:opacity-50"
                      style={{ background: "#EB785C", color: "#ffffff" }}
                    >
                      {nurtureSending ? "Sending…" : `Send now`}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Daily sparkline */}
      {trend.length > 0 && (
        <section>
          <h3
            className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3"
            style={{ color: "var(--mek-text-2)" }}
          >
            Daily activity
          </h3>
          <div
            className="rounded-lg border p-6 bg-white"
            style={{ borderColor: "var(--mek-border)" }}
          >
            <div className="flex items-end gap-1 h-32" data-testid="analytics-sparkline">
              {trend.map((d) => (
                <div
                  key={d.day}
                  className="flex-1 flex flex-col items-center justify-end gap-0.5 min-w-0"
                  title={`${d.day}\nLanding: ${d.landing_view}\nScenarios: ${d.scenario_view}`}
                >
                  <div
                    className="w-full rounded-sm"
                    style={{
                      height: `${(d.scenario_view / maxVal) * 100}%`,
                      background: "var(--mme-teal)",
                      minHeight: d.scenario_view > 0 ? "2px" : "0",
                    }}
                  />
                  <div
                    className="w-full rounded-sm"
                    style={{
                      height: `${(d.landing_view / maxVal) * 30}%`,
                      background: "var(--mme-coral)",
                      minHeight: d.landing_view > 0 ? "2px" : "0",
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs" style={{ color: "var(--mek-text-3)" }}>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: "var(--mme-teal)" }} />
                Scenario views
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm inline-block" style={{ background: "var(--mme-coral)" }} />
                Landing views
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Top scenarios */}
      <section>
        <h3
          className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3"
          style={{ color: "var(--mek-text-2)" }}
        >
          Top scenarios by views
        </h3>
        {(data.top_scenarios || []).length === 0 ? (
          <p
            className="text-sm italic px-4 py-6 rounded-md border border-dashed text-center"
            style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-3)" }}
          >
            No scenario views yet in this window.
          </p>
        ) : (
          <div className="space-y-1">
            {(data.top_scenarios || []).map((s, idx) => (
              <div
                key={s.slug}
                className="flex items-center gap-3 py-2 border-b last:border-b-0"
                style={{ borderColor: "var(--mek-border)" }}
                data-testid={`analytics-top-${s.slug}`}
              >
                <span
                  className="text-xs font-mono w-6 text-right"
                  style={{ color: "var(--mek-text-3)" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className="text-sm font-medium truncate"
                    style={{ color: "var(--mek-text)" }}
                  >
                    {s.title}
                  </p>
                  <p
                    className="text-xs truncate"
                    style={{ color: "var(--mek-text-3)" }}
                  >
                    {s.category}
                  </p>
                </div>
                <div className="flex-1 max-w-[40%]">
                  <div
                    className="h-2 rounded-full overflow-hidden"
                    style={{ background: "rgba(31, 85, 89, 0.10)" }}
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${(s.views / maxScenarioViews) * 100}%`,
                        background: "var(--mme-teal)",
                      }}
                    />
                  </div>
                </div>
                <span
                  className="text-sm font-semibold w-12 text-right tabular-nums"
                  style={{ color: "var(--mek-text)" }}
                >
                  {s.views}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}


/**
 * ABTestPanel — single-experiment landing-page A/B testing.
 *
 * Lets the operator define two hero-headline variants (main + accent words),
 * toggle the experiment on/off, and see live impressions / checkouts /
 * conversion per variant. Picks a winner only once each variant has at
 * least 30 impressions to avoid celebrating noise.
 */
function ABTestPanel() {
  const [experiment, setExperiment] = useState(null);
  const [results, setResults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [{ data: exp }, { data: res }] = await Promise.all([
        api.get("/admin/experiment"),
        api.get("/admin/experiment/results"),
      ]);
      setExperiment(exp);
      setResults(res);
    } catch (_) {
      toast.error("Couldn't load the A/B test config.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const updateVariant = (idx, field, value) => {
    setExperiment((prev) => {
      const variants = prev.variants.map((v, i) => (i === idx ? { ...v, [field]: value } : v));
      return { ...prev, variants };
    });
  };

  const save = async () => {
    if (saving || !experiment) return;
    setSaving(true);
    try {
      await api.put("/admin/experiment", {
        active: experiment.active,
        variants: experiment.variants.map((v) => ({
          key: (v.key || "").trim(),
          headline_main: (v.headline_main || "").trim(),
          headline_accent: (v.headline_accent || "").trim(),
        })),
      });
      toast.success(experiment.active ? "Experiment is live." : "Saved (experiment paused).");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the experiment.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !experiment) {
    return <p className="text-sm italic" style={{ color: "var(--mek-text-3)" }}>Loading A/B test…</p>;
  }

  const variants = experiment.variants || [];
  const resultRows = results?.variants || [];
  const winner = results?.winner;

  return (
    <div className="space-y-8" data-testid="ab-test-panel">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-1" style={{ color: "var(--mek-text-2)" }}>
          Landing-page A/B test
        </p>
        <p className="text-sm" style={{ color: "var(--mek-text-2)" }}>
          One experiment at a time. Visitors are randomly assigned a hero headline (50/50) on first visit and see the same variant every time after.
        </p>
      </div>

      {/* Active toggle */}
      <div
        className="rounded-lg border p-5 flex items-center justify-between gap-4 flex-wrap"
        style={{ borderColor: "var(--mek-border)", background: experiment.active ? "rgba(74, 97, 63, 0.06)" : "white" }}
      >
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: experiment.active ? "#4A613F" : "var(--mek-text-3)" }}>
            {experiment.active ? "Live" : "Paused"}
          </p>
          <p className="text-sm mt-1" style={{ color: "var(--mek-text-2)" }}>
            {experiment.active
              ? "Visitors are seeing one of the two variants below."
              : "Landing page is showing its default copy. Flip on to start the test."}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExperiment((p) => ({ ...p, active: !p.active }))}
          data-testid="ab-toggle-active"
          className="text-xs px-4 py-2 rounded-full border transition"
          style={{
            borderColor: experiment.active ? "var(--mme-terracotta, #8B3E3E)" : "var(--mek-text)",
            color: experiment.active ? "var(--mme-terracotta, #8B3E3E)" : "var(--mek-text)",
          }}
        >
          {experiment.active ? "Pause experiment" : "Activate experiment"}
        </button>
      </div>

      {/* Variant editor */}
      <div className="grid md:grid-cols-2 gap-5">
        {variants.map((v, idx) => {
          const row = resultRows.find((r) => r.key === v.key);
          const isWinner = winner === v.key;
          return (
            <div
              key={idx}
              className="rounded-lg border p-5 bg-white space-y-4"
              style={{
                borderColor: isWinner ? "#4A613F" : "var(--mek-border)",
                boxShadow: isWinner ? "0 0 0 3px rgba(74,97,63,0.12)" : "none",
              }}
              data-testid={`ab-variant-card-${idx}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                  Variant {idx + 1}
                </p>
                {isWinner && (
                  <span
                    className="text-[10px] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(74, 97, 63, 0.15)", color: "#4A613F" }}
                  >
                    ● Winner
                  </span>
                )}
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--mek-text-3)" }}>
                  Variant key
                </label>
                <input
                  type="text"
                  value={v.key || ""}
                  onChange={(e) => updateVariant(idx, "key", e.target.value)}
                  placeholder="e.g. control"
                  data-testid={`ab-variant-key-${idx}`}
                  className="mt-1 w-full px-3 py-2 rounded border text-sm"
                  style={{ borderColor: "var(--mek-border)" }}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--mek-text-3)" }}>
                  Headline (main)
                </label>
                <input
                  type="text"
                  value={v.headline_main || ""}
                  onChange={(e) => updateVariant(idx, "headline_main", e.target.value)}
                  placeholder="The manager toolkit"
                  data-testid={`ab-variant-main-${idx}`}
                  className="mt-1 w-full px-3 py-2 rounded border text-sm"
                  style={{ borderColor: "var(--mek-border)" }}
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: "var(--mek-text-3)" }}>
                  Headline (gold-highlighted accent)
                </label>
                <input
                  type="text"
                  value={v.headline_accent || ""}
                  onChange={(e) => updateVariant(idx, "headline_accent", e.target.value)}
                  placeholder="nobody handed you."
                  data-testid={`ab-variant-accent-${idx}`}
                  className="mt-1 w-full px-3 py-2 rounded border text-sm"
                  style={{ borderColor: "var(--mek-border)" }}
                />
              </div>

              {/* Live results */}
              <div
                className="pt-3 border-t grid grid-cols-3 gap-3"
                style={{ borderColor: "var(--mek-border)" }}
              >
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                    Impressions
                  </p>
                  <p className="text-xl font-serif mt-0.5" style={{ color: "var(--mek-text)" }}>
                    {row?.impressions ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                    Checkouts
                  </p>
                  <p className="text-xl font-serif mt-0.5" style={{ color: "var(--mek-text)" }}>
                    {row?.checkouts ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--mek-text-3)" }}>
                    Conversion
                  </p>
                  <p className="text-xl font-serif mt-0.5" style={{ color: "var(--mek-text)" }}>
                    {row ? `${row.conversion_pct}%` : "—"}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {!winner && resultRows.some((r) => r.impressions > 0) && (
        <p className="text-xs italic" style={{ color: "var(--mek-text-3)" }}>
          We&apos;ll crown a winner once each variant has at least {results?.min_sample_for_winner || 30} impressions — keeps us from celebrating noise.
        </p>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="ab-save-btn"
          className="text-sm px-5 py-2 rounded-full transition disabled:opacity-50"
          style={{ background: "var(--mek-text)", color: "var(--mek-bg)" }}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          onClick={refresh}
          data-testid="ab-refresh-btn"
          className="text-sm px-5 py-2 rounded-full border transition"
          style={{ borderColor: "var(--mek-border)", color: "var(--mek-text-2)" }}
        >
          Refresh results
        </button>
      </div>
    </div>
  );
}

